// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import MagIcon from "@ces/XDL/Icons/MagIcon";
import { parseBoolean } from "@ces/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ImageModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: ProductModel;
    image: ImageModel;
    useLargeImage?: boolean;
    onClick?: () => void;
    extendedStyles?: ProductPrimaryImageStyles;
    // XNG-Change
    showZoom?: Boolean;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

export interface ProductPrimaryImageStyles {
    centeringWrapper?: InjectableCss;
    /**
     * @deprecated Not used anymore
     */
    hiddenWrapper?: InjectableCss;
    /**
     * @deprecated Not used anymore
     */
    spinner?: LoadingSpinnerProps;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
    // XNG-Chnage
    magIcon?: IconPresentationProps;
}

export const productPrimaryImageStyles: ProductPrimaryImageStyles = {
    centeringWrapper: {
        css: css`
            min-height: 300px;
            margin: 10px 0;
            display: flex;
            align-items: center;
            justify-content: center;
            img {
                height: auto;
                max-width: 100%;
            }
        `,
    },
    image: {
        css: css`
            margin: auto;
            cursor: pointer;
        `,
    },
    img: {
        css: css`
            cursor: pointer;
        `,
    },
};

const ProductPrimaryImage = ({
    productSettings,
    product,
    image,
    useLargeImage,
    onClick,
    extendedStyles,
    // XNG-Change
    showZoom = false,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productPrimaryImageStyles, extendedStyles));
    // XNG-Change
    const [invalidImage, setInvalidImage] = useState(false);

    if (!product) {
        return null;
    }

    useEffect(() => {
        setInvalidImage(false);
    }, [image]);

    useEffect(() => {
        if ((window as any).sirvScriptAdded) {
            return;
        }

        if (
            productSettings.imageProvider !== "SIRV" ||
            !product.images ||
            product.images.every(o => o.imageType !== "360")
        ) {
            return;
        }

        const script = document.createElement("script");
        script.src = "https://scripts.sirv.com/sirv.js";
        script.async = true;

        document.body.appendChild(script);
        (window as any).sirvScriptAdded = true;
    }, [productSettings.imageProvider, product]);

    const imageWrapperClickHandler = () => {
        onClick?.();
    };

    const imageWrapperKeyHandler: React.KeyboardEventHandler<HTMLImageElement> = event => {
        event.preventDefault();
        event.stopPropagation();
        if (event.key === "Enter") {
            onClick?.();
        }
    };

    const path = useLargeImage
        ? image.largeImagePath || image.mediumImagePath
        : image.mediumImagePath || image.largeImagePath;
    if (!path) {
        return null;
    }

    // XNG-Change
    const canZoomImage = showZoom && !parseBoolean(product.properties.isNotFoundMediumImage) && !invalidImage;

    // XNG-Change add mag. icon
    return (
        <>
            {image.imageType === "Static" && (
                <StyledWrapper
                    {...styles.centeringWrapper}
                    onClick={canZoomImage ? imageWrapperClickHandler : undefined}
                >
                    <Img
                        {...styles.img}
                        key={image.id}
                        src={path}
                        altText={image.imageAltText}
                        loading="eager"
                        data-test-selector="productDetails_mainImage"
                        onKeyPress={imageWrapperKeyHandler}
                        tabIndex={0}
                        onError={() => {
                            setInvalidImage(true);
                        }}
                    />
                    {canZoomImage && <IconMemo src={MagIcon} {...styles.magIcon} onClick={imageWrapperClickHandler} />}
                </StyledWrapper>
            )}
            {/* {image.imageType === "360" && productSettings.imageProvider === "SIRV" && (
                <div style={{ minHeight: 300 }} onClick={imageWrapperClickHandler}>
                    <div className="Sirv" key={image.id} data-src={image.mediumImagePath} />
                </div>
            )} */}
            {/* XNG-Changes: CES-723 */}
            {image.imageType === "360" && (
                <div style={{ minHeight: "100%" }} onClick={imageWrapperClickHandler}>
                    <iframe
                        src={image.mediumImagePath}
                        width="100%"
                        height="100%"
                        frameBorder={0}
                        style={{ minHeight: 320 }}
                        allowFullScreen
                    />
                    {!image.mediumImagePath.endsWith(".spin") && canZoomImage && (
                        <IconMemo src={MagIcon} {...styles.magIcon} onClick={imageWrapperClickHandler} />
                    )}
                </div>
            )}
        </>
    );
};

export default connect(mapStateToProps)(ProductPrimaryImage);
